import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { SyncLoader } from "react-spinners";
import ReactMarkdown from "react-markdown";
import ArrowBackIcon from "../../../../assets/images/arrow_back_black_icon.svg?react";
import ArrowBackWhiteIcon from "../../../../assets/images/arrow_back_icon.svg?react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { ResourceTypeEnum } from "../../../../core/enum/resource_type_enum";
import { useGetResourceById } from "../../domain/hooks/use_get_resource_by_id";
import { useMarkResourceAsViewed } from "../../domain/hooks/use_mark_resource_as_viewed";
import SecureVideoPlayer from "../components/secure_video_player";
import SecureAudioPlayer from "../components/secure_audio_player";
import SecureTextReader from "../components/secure_text_reader";
import ResourceFavoriteButton from "../components/resource_favorite_button";
import { markdownComponents } from "../components/markdown_components";
import { UserInterface } from "../../../../core/components/user/domain/interfaces/user_interface";
import { CareerStatusEnum } from "../../../../core/enum/career_status_enum";
import { useTranslation } from "../../../../i18n/useTranslation";

const ResourcePage: React.FC = () => {
    const { t } = useTranslation();
    const { resourceId } = useParams();
    const navigate = useNavigate();
    const theme = ThemeConstants();
    const { careerStatus, company } = useOutletContext<UserInterface>();
    const { data: resource, error, isPending, refetch } = useGetResourceById(resourceId);
    const { mutate: markCompleted } = useMarkResourceAsViewed();

    useEffect(() => {
        if (company?.permissions.course === true && careerStatus === CareerStatusEnum.Start) {
            navigate('/career/start', { replace: true });
        }
    }, [careerStatus, company?.permissions.course, navigate]);

    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = '#FFFFFF';
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    const handleCompleted = useCallback(
        (id: string) => markCompleted(id),
        [markCompleted]
    );

    const renderError = (msg: string) => (
        <div className="flex flex-col flex-1 justify-center gap-4 items-center self-center">
            <span className="text-sm text-medium text-center">{msg}</span>
            <button
                className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                style={{ background: theme.UvibesPink }}
                onClick={() => refetch()}
            >
                {t('common.button.retry')}
            </button>
        </div>
    );

    const renderResource = () => {
        if (!resource) return null;

        switch (resource.type) {
            case ResourceTypeEnum.VIDEO:
                if (!resource.content) {
                    return (
                        <div className="flex flex-col flex-1 justify-center items-center self-center">
                            <span className="text-sm text-medium text-center">{t('course.resource.video_unavailable')}</span>
                        </div>
                    );
                }
                return (
                    <>
                        <SecureVideoPlayer resource={resource} onCompleted={handleCompleted} />
                        <div className="flex flex-col gap-3 items-center pb-[5.6dvh] max-lg:px-[3%] lg:px-[1%]">
                            <h1 className="font-bold text-2xl leading-tight text-center" style={{ color: theme.UvibesPink }}>{resource.title}</h1>
                            <div className="prose prose-lg max-w-153">
                                <ReactMarkdown components={markdownComponents}>
                                    {resource.description ?? ""}
                                </ReactMarkdown>
                            </div>
                        </div>
                    </>
                );

            case ResourceTypeEnum.AUDIO:
                if (!resource.content) {
                    return (
                        <div className="flex flex-col flex-1 justify-center items-center self-center">
                            <span className="text-sm text-medium text-center">{t('course.resource.audio_unavailable')}</span>
                        </div>
                    );
                }
                return <SecureAudioPlayer resource={resource} onCompleted={handleCompleted} />;

            case ResourceTypeEnum.ARTICLE:
            default:
                return <SecureTextReader resource={resource} onCompleted={handleCompleted} />;
        }
    };

    return (
        <div className="min-h-dvh flex flex-col gap-8 items-center relative bg-white">
            <div className="flex w-full items-center justify-between absolute top-0 left-0 pt-[5.6dvh] max-lg:px-[3%] lg:px-[1%] z-1">
                <button className='cursor-pointer self-start w-6 h-6 flex items-center justify-center' onClick={() => navigate(-1)}>
                    {resource?.type === ResourceTypeEnum.ARTICLE || resource?.type === ResourceTypeEnum.VIDEO
                        ? <ArrowBackWhiteIcon className='w-4.25 h-auto'/>
                        : <ArrowBackIcon color='black' className='w-4.25 h-auto'/>
                    }
                </button>
                {resource && resource.type !== ResourceTypeEnum.AUDIO && (
                    <ResourceFavoriteButton
                        resourceId={resource._id}
                        initialFavoriteId={resource._favoriteId}
                        outlineColor="#FFFFFF"
                    />
                )}
            </div>

            {(isPending || error) && (<div className="absolute top-0 left-0 w-full h-full flex justify-center flex-col items-center gap-3">
                {isPending
                    ? <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
                    : error
                        ? renderError(t('common.error.occurred'))
                        : null
                }
            </div>)}

            {resource && renderResource()}
        </div>
    );
};

export default ResourcePage;
